import { ApiError, apiFetch, apiRootUrl, isOffline } from "@/lib/apiClient";

export const HEALTH_PATH = "/health";

export type HealthStatus = "healthy" | "maintenance";

export interface HealthReport {
  status: HealthStatus;
  message: string | null;
  estimatedEndsAt: string | null;
  raw: unknown;
}

const HEALTHY_WORDS = ["healthy", "ok", "up", "pass", "passing", "online", "ready"];
const UNHEALTHY_WORDS = [
  "unhealthy",
  "maintenance",
  "maintaining",
  "down",
  "fail",
  "failing",
  "offline",
  "unavailable",
];

const OUTAGE_STATUSES = [0, 502, 503, 504];

function readString(source: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

function readBoolean(source: Record<string, unknown>, keys: string[]): boolean | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "boolean") return value;
  }
  return null;
}

function statusFromWord(word: string): HealthStatus | null {
  const normalized = word.trim().toLowerCase();
  if (UNHEALTHY_WORDS.some((w) => normalized.includes(w))) return "maintenance";
  if (HEALTHY_WORDS.some((w) => normalized === w || normalized.startsWith(`${w} `)))
    return "healthy";
  return null;
}

export function parseHealth(raw: unknown): HealthReport {
  const report: HealthReport = {
    status: "healthy",
    message: null,
    estimatedEndsAt: null,
    raw,
  };

  if (typeof raw === "string") {
    report.status = statusFromWord(raw) ?? "healthy";
    return report;
  }

  if (!raw || typeof raw !== "object") return report;

  const source = raw as Record<string, unknown>;
  report.message = readString(source, ["message", "detail", "description", "reason"]);
  report.estimatedEndsAt = readString(source, [
    "estimatedEndsAt",
    "estimatedRestoreAt",
    "until",
    "endsAt",
  ]);

  const maintenance = readBoolean(source, ["maintenance", "isUnderMaintenance", "inMaintenance"]);
  if (maintenance !== null) {
    report.status = maintenance ? "maintenance" : "healthy";
    return report;
  }

  const healthy = readBoolean(source, ["healthy", "isHealthy", "success", "ok"]);
  if (healthy !== null) {
    report.status = healthy ? "healthy" : "maintenance";
    return report;
  }

  const word = readString(source, ["status", "state", "health"]);
  if (word) report.status = statusFromWord(word) ?? "healthy";
  return report;
}

export function isOutage(error: unknown): boolean {
  if (isOffline()) return false;
  return error instanceof ApiError && OUTAGE_STATUSES.includes(error.status);
}

export const system = {
  health: (signal?: AbortSignal) =>
    apiFetch<unknown>(apiRootUrl(HEALTH_PATH), { signal }).then(parseHealth),
};
